import { I18n, i18n } from '@lingui/core';
import { Badge } from '@mantine/core';
import { BillingAddon, BillingPlan } from '../Types/Billing';
import BookingState from '../Types/Enums/BookingState';
import { CustomAttributeGroupDTO, CustomAttributeWithGroupNameDTO } from '../Types/CustomAttributeT';
import { EntityReferenceDTO } from '../Types/LogT';
import { AgencyTemplateDTO, MessageDTO, MessageTemplateDto } from '../Types/MessageT';
import { EventKind, CustomEventState } from '../Types/EventT';
import { QuestionnaireTemplateDto } from '../Types/QuestionnaireT';
import { DocumentLayoutPlacement } from './PdfS';
import { Contact, Country } from './ContactS';
import { Resource } from '../Types/ResourceT';
import SemanticFormStyle from '../Types/Enums/SemanticFormStyle';
import { LocationDto } from '../Types/LocationT';
import { LocationS } from './LocationS';
import BookingEvent, { EventDate } from '../Types/Event';
import { EventS } from './EventS';
import { GoogleAccountDTO } from '../Types/GoogleT';
import { Currency } from '../Types/BookitupDocument';
import { Label } from 'reactstrap';
import { Language } from './DocumentT';
import DocumentLanguageS from './DocumentLanguageS';

export interface KeyValuePair {
  key: string;
  value: string;
}

export interface LabelValuePair {
  label: string;
  value: string;
}

const fontOptions = [
  { label: 'Helvetica', value: 'Helvetica' },
  { label: 'OpenSans', value: 'OpenSans' },
  { label: 'Calibri', value: 'Calibri' },
  { label: 'Baskerville', value: 'Baskerville' },
  { label: 'Raleway', value: 'Raleway' },
  { label: 'DejaVuSans', value: 'DejaVuSans' },
  { label: 'Montserrat', value: 'Montserrat' },
  { label: 'Share', value: 'Share' },
  { label: 'Frutiger', value: 'Frutiger' },
  { label: 'Satoshi', value: 'Satoshi' },
  { label: 'Syne', value: 'Syne' },
];

const documentLayoutMarginTopOptions = [
  { label: 'kein Abstand', value: '0' },
  { label: '1', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4', value: '4' },
  { label: '5', value: '5' },
  { label: '6', value: '6' },
  { label: '7', value: '7' },
  { label: '8', value: '8' },
  { label: '9', value: '9' },
  { label: '10', value: '10' },
];

const documentLayoutBottomMarginPageScopeOptions = [
  { label: 'kein Abstand', value: 'NONE' },
  { label: 'Nur auf der 1. Seite', value: 'FIRST_PAGE' },
  { label: 'Nur ab der 2. Seite', value: 'FROM_SECOND_PAGE' },
  { label: 'Alle Seiten', value: 'ALL_PAGES' },
];

const getContactNameOptions = (infoBoxPlacement: DocumentLayoutPlacement) => [
  { label: 'Firmenname', value: 'COMPANY' },
  { label: 'Künstlername', value: 'ALIAS' },
  { label: 'keine Name', value: 'NONE', disabled: infoBoxPlacement !== 'RIGHT' },
];

const logoSizeOptions = [
  { label: 'XS', value: 'EXTRA_SMALL' },
  { label: 'S', value: 'SMALL' },
  { label: 'M', value: 'MEDIUM' },
  { label: 'L', value: 'BIG' },
  { label: 'XL', value: 'FULLSIZE' },
];

const headerFontSizeOptions = [
  { label: 'Schriftgröße 16 (Standard)', value: '16' },
  { label: 'Schriftgröße 15', value: '15' },
  { label: 'Schriftgröße 14', value: '14' },
  { label: 'Schriftgröße 13', value: '13' },
  { label: 'Schriftgröße 12', value: '12' },
  { label: 'Schriftgröße 11', value: '11' },
  { label: 'Schriftgröße 10', value: '10' },
  { label: 'Schriftgröße 9', value: '9' },
];

const defaultFontSizeOptions = [
  { label: 'Schriftgröße 9 (Standard)', value: '9' },
  { label: 'Schriftgröße 8', value: '8' },
  { label: 'Schriftgröße 7', value: '7' },
  { label: 'Schriftgröße 6', value: '6' },
];

const subtextFontSizeOptions = [
  { label: 'Schriftgröße 8 (Standard)', value: '8' },
  { label: 'Schriftgröße 7', value: '7' },
  { label: 'Schriftgröße 6', value: '6' },
  { label: 'Schriftgröße 5', value: '5' },
];

const profileInfoFontSizeOptions = [
  { label: 'Schriftgröße 9', value: '9' },
  { label: 'Schriftgröße 8', value: '8' },
  { label: 'Schriftgröße 7 (Standard)', value: '7' },
  { label: 'Schriftgröße 6', value: '6' },
  { label: 'Schriftgröße 5', value: '5' },
];

const boxPlacementOptions = () => {
  const opts = [];
  opts.push({
    value: 'NONE',
    label: 'keine Infos',
    description:
      'Diese Option ist praktisch, wenn du einen Hintergrund hochlädst, der die notwendigen Informationen bereits enthält, um eine Text-Überschneidung zu vermeiden.',
  });
  opts.push({
    value: 'RIGHT',
    label: 'Oben rechts',
    description:
      'Eine Auswahl der Unternehmensinfos wird kompakt oben rechts dargestellt. Hierbei werden die Zusatzangaben für Geschäftsführer etc. NICHT dargestellt.',
  });
  opts.push({
    value: 'CENTER',
    label: 'Unten zentriert',
    description: 'Alle Unternehmensinfos werden im Fußbereich vom Dokument untereinander, zentriert dargestellt.',
  });
  opts.push({
    value: 'JUSTIFIED',
    label: 'Unten blockweise',
    description: 'Alle Unternehmensinfos werden im Fußbereich vom Dokument nebeneinander, blockweise dargestellt.',
  });
  return opts;
};

const pageNumberPositionOptions = [
  { label: 'Oben', value: 'TOP' },
  { label: 'Mittig', value: 'MIDDLE' },
  { label: 'Unten', value: 'BOTTOM' },
];

const eventSortingOptions = [
  { label: 'Chronologisch', value: 'chronological' },
  { label: 'Letzte Anfragen', value: 'createdAt' },
];

const recommendationOrderOptions = [
  { label: 'Alle', value: 'chronological' },
  { label: 'Nur übermittelte', value: 'createdAt' },
  { label: 'Nur nicht übermittelte', value: 'chronological' },
];

const getBookingStateOptions = (recommendationId?: string) => {
  const options = [];
  options.push(
    { label: 'Anfrage', value: BookingState.OPEN },
    { label: 'Option', value: BookingState.OFFERED },
    { label: 'Gebucht', value: BookingState.BOOKED },
  );
  if (!recommendationId) {
    options.push({ label: 'Nicht verfügbar', value: BookingState.UNAVAILABLE });
  }
  options.push({ label: 'Annulliert', value: BookingState.CANCELED });
  return options;
};

const servicesOptions = [
  { label: 'Fotograf:in', value: 'Fotograf:in' },
  { label: 'Videograf:in', value: 'Videograf:in' },
  { label: 'DJ', value: 'DJ' },
  { label: 'Sänger:in', value: 'Sänger:in' },
  { label: 'Dekorateuer:in', value: 'Dekorateuer:in' },
  { label: 'Florist:in', value: 'Florist:in' },
  { label: 'Location', value: 'Location' },
  { label: 'Catering', value: 'Catering' },
  { label: 'Freier Redner', value: 'Freier Redner' },
  { label: 'Zauberer', value: 'Zauberer' },
  { label: 'Saxophonist', value: 'Saxophonist' },
  { label: 'Make-up & Hair Styling', value: 'Make-up & Hair Styling' },
  { label: 'Eventtechnik', value: 'Eventtechnik' },
  { label: 'Fotobox-Verleih', value: 'Fotobox-Verleih' },
  { label: 'Agentur', value: 'Agentur' },
  { label: 'Band', value: 'Band' },
  { label: 'Sonstiges', value: 'Sonstiges' },
];

const getServiceOptions = (excludeValue?: string | null) => {
  if (!excludeValue) return servicesOptions;
  return servicesOptions.filter((option) => option.value !== excludeValue);
};

const howYouKnowUsOptions = [
  { label: 'Anzeige bei Facebook', value: 'FACEBOOK' },
  { label: 'Anzeige bei Instagram', value: 'INSTAGRAM' },
  { label: 'Anzeige bei YouTube', value: 'YOUTUBE' },
  { label: 'lexoffice', value: 'LEX_OFFICE' },
  { label: 'Suchmaschine', value: 'SEARCH_ENGINE' },
  { label: 'Berufsverband Discjockeys', value: 'DISCJOCKEYS_ASSOCIATION' },
  { label: 'DJ CONVENTION', value: 'DJ_WORKSHOP' },
  { label: 'DJ Talk', value: 'DJ_TALK' },
  { label: 'Frau Immer & Herr Ewig', value: 'FRAU_IMMER_HERR_EWIG' },
  { label: 'Persönliche Empfehlung', value: 'PERSONAL_RECOMMENDATION' },
  { label: 'Sonstiges', value: 'OTHER' },
];

const billingAddons: BillingAddon[] = [
  {
    name: 'Resource-Planning',
    description:
      'Mit dem "Resource Planning" Addon kannst Du Vermietartikel verwalten, in Deinen Events einplanen und in einer Zeitleiste Überschneidungen, sowie Überbuchungen sofort erkennen. Du siehst schon bei der Anfrage, welche Ressourcen noch verfügbar sind und kannst zudem nichts mehr vergessen.',
    price: {
      Monthly: [
        {
          from: 1,
          to: 10,
          price: 10,
        },
        {
          from: 11,
          to: 50,
          price: 15,
        },
        {
          from: 51,
          to: 100,
          price: 20,
        },
        {
          from: 101,
          to: 250,
          price: 25,
        },
        {
          from: 251,
          to: 500,
          price: 30,
        },
      ],
      Yearly: [
        {
          from: 1,
          to: 10,
          price: 5,
        },
        {
          from: 11,
          to: 50,
          price: 10,
        },
        {
          from: 51,
          to: 100,
          price: 15,
        },
        {
          from: 101,
          to: 250,
          price: 20,
        },
        {
          from: 251,
          to: 500,
          price: 25,
        },
      ],
    },
  },
];

const billingPlans: BillingPlan[] = [
  {
    name: 'Starter',
    internalName: 'foo',
    licence: 'BASIC',
    free: true,
    price: {
      Monthly: 0,
      Yearly: 0,
    },
    topFeaturesText: 'Verwalte Deine Events mit diesen Funktionen:',
    features: [
      'Event-Übersicht',
      '1 Kontaktformular',
      'Verfügbarkeits-Check',
      '1 privaten Kalender prüfen',
      'Angebote & Rechnungen',
      'E-Mails im Dienstleister-Netzwerk',
      'E-Mail Postfach',
      'Kalender-Abo über Deine Bookings',
      'Kalender-Abo über Deine Vermittlungen',
    ],
  },
  {
    name: 'Essentials',
    internalName: 'Pro',
    licence: 'PRO',
    price: {
      Monthly: 16,
      Yearly: 13,
    },
    topFeaturesText: 'Alle Funktionen von Starter, plus:',
    topFeaturesComponent: (
      <Badge mb="md" radius="sm" sx={{ color: 'white' }} variant="gradient">
        inkl. Mustervertrag für DJs
      </Badge>
    ),
    features: [
      'Eigene Felder',
      'Fragebögen',
      'Verträge',
      'Dokument-Vorlagen',
      'Abschlagsrechnungen',
      'Schlussrechnungen',
      'Zahlungen erfassen',
      'Aufgaben',
      'Dateien hochladen',
      'lexoffice Anbindung',
      'sevDesk Anbindung',
    ],
  },
  {
    name: 'Standard',
    internalName: 'Interactive',
    licence: 'INTERACTIVE',
    price: {
      Monthly: 30,
      Yearly: 25,
    },
    topFeaturesText: 'Alle Funktionen von Essentials, plus:',
    features: [
      '∞ Kontaktformular erstellen',
      '∞ externe private Kalender',
      'Kontakte-Sync zu Google',
      'Aufgaben-Sync zu Google',
      'Workflows / Automationen',
      'Eigene Event-Status',
      'Agenturmodus',
      'Agenturvorlagen',
      'Aut. Verf.-Prüfung im Netzwerk',
      'Account-Freigaben für Teams',
      'Eigene Vorlagen für E-Mails',
      'E-Mails planen',
      'Statistiken',
      'Zapier-Anbindung in Kontaktformular',
      'Online Kundenportal',
      'Online Verträge',
      'Online Fragebögen',
      'Online Fragebögen Sync mit eigenen Feldern',
      'Online Angebote',
      'Online Auftragsbestätigungen',
      'Online Rechnungen',
      'Online PDF Freigabe',
    ],
  },
];

const billingIntervals = [
  { label: 'Monatlich', value: 'Monthly' },
  { label: 'Jährlich', value: 'Yearly' },
];

const paymentUnitOptions = [
  { label: '%', value: 'PERCENTAGE' },
  { label: '€', value: 'EURO' },
];

const getDocumentTypeOptions = (includeReminder = true) => {
  const opts = [
    { label: 'Angebot', value: 'OFFER' },
    { label: 'Auftragsbestätigung', value: 'CONFIRMATION' },
    { label: 'Rechnung', value: 'INVOICE' },
    { label: 'Abschlagsrechnung', value: 'PARTIAL_INVOICE' },
  ];

  if (includeReminder) {
    opts.push({ label: 'Mahnung', value: 'REMINDER' });
  }
  opts.push({ label: 'Rechnungskorrektur', value: 'CANCEL' });

  return opts;
};

const discountUnitOptions = [
  { label: '%', value: 'PERCENT' },
  { label: '€', value: 'ABSOLUTE' },
];

const getDiscountUnitOptions = (currencySymbol: string) => [
  { label: '%', value: 'PERCENT' },
  { label: currencySymbol, value: 'ABSOLUTE' },
];

const currencyOptions = [
  { label: '€', value: 'EUR' },
  { label: 'CHF', value: 'CHF' },
];

const netGrossOptions = [
  { label: 'Brutto', value: 'gross' },
  { label: 'Netto', value: 'net' },
];

const documentLanguageOptions: { label: string; value: Language }[] = [
  { label: DocumentLanguageS.translate('german', 'language.german'), value: 'german' },
  { label: DocumentLanguageS.translate('german', 'language.english'), value: 'english' },
];

const taxRateOptions = [
  { label: '19', value: '19' },
  { label: '7', value: '7' },
  { label: '0', value: '0' },
];

const positionTypeOptions = [
  { label: 'Normal', value: null },
  { label: 'Freitext', value: 'FREE_TEXT' },
  { label: 'Gruppe', value: 'GROUP' },
  { label: 'Ressource', value: 'RESOURCE' },
];

const positionUnitSuggestions = ['Stk.', 'Std.', 'km', 'Tag(e)', 'kg', 'm', 'm²', 'Personen', 'l'];

const positionAmountSuggestions = [
  { label: 'Event Uhrzeit', value: '{event_hours}' },
  { label: 'Location Entfernung', value: '{location_distance}' },
];

const getTaxRatesByCountry = (country: Country, currentValue?: number) => {
  if (country === 'CH') return getTaxRateOptions('CHF', currentValue);
  return getTaxRateOptions('EUR', currentValue);
};

const getTaxRateOptions = (currency: Currency, currentValue?: number) => {
  const opts = [];
  if (currency === 'EUR') {
    opts.push({ label: '19', value: '19' }, { label: '7', value: '7' });
  } else {
    opts.push({ label: '8.1', value: '8.1' }, { label: '3.8', value: '3.8' }, { label: '2.6', value: '2.6' });
  }
  opts.push({ label: '0', value: '0' });

  if (currentValue) {
    const currentValuePresent = opts.find((o) => o.value === currentValue.toString());
    if (!currentValuePresent) {
      opts.push({ label: currentValue.toString(), value: currentValue.toString() });
      opts.sort((a, b) => Number(a.value) - Number(b.value));
    }
  }
  return opts;
};

const shippingTypeOptions = [
  { label: 'Kein Lieferdatum anzeigen', value: 'none' },
  { label: 'Lieferdatum', value: 'delivery' },
  { label: 'Leistungsdatum', value: 'service' },
  { label: 'Lieferzeitraum', value: 'deliveryperiod' },
  { label: 'Leistungszeitraum', value: 'serviceperiod' },
];

const getAccountingWebsiteOptions = (i18n: I18n) => [
  { label: i18n._('profile.accoutingTool.lexOffice'), value: 'LexOffice' },
  { label: i18n._('profile.accoutingTool.sevDesk'), value: 'sevDesk' },
  { label: i18n._('profile.accoutingTool.datev'), value: 'DATEV' },
  { label: i18n._('Accountable'), value: 'Accountable' },
  { label: i18n._('Papierkram'), value: 'Papierkram' },
  { label: 'E-Mail', value: 'E-Mail' },
  { label: i18n._('profile.accoutingTool.other'), value: 'Other' },
];

const documentStatusOptions = [
  { label: 'Alle', value: 'undefined' },
  { label: 'Entwurf', value: 'true' },
  { label: 'Fertig', value: 'false' },
];

const documentInvoicePaymentStatusOptions = [
  { label: 'Alle', value: 'undefined' },
  { label: 'Zahlung erhalten', value: 'true' },
  { label: 'keine Zahlung', value: 'false' },
];

const removeEmptyValues = (obj: any) =>
  Object.keys(obj).reduce((acc, key) => {
    const _acc = acc as any;
    if (obj[key] !== undefined) _acc[key] = obj[key];
    return _acc;
  }, {});

const resourceTypeOptions = [
  {
    label: 'Item',
    value: 'ITEM',
    description: 'Eine einzelne Ressource, wie ein Kerzenständer oder ein Lautsprecher.',
  },
  {
    label: 'Set',
    value: 'KIT',
    description: 'Eine logische Sammlung von Ressourcen, wie ein Hochzeitspaket für bis zu 100 Gästen.',
  },
  {
    label: 'Case',
    value: 'CASE',
    description:
      'Eine Sammlung von Ressourcen, die nur zusammen genutzt werden, wie eine Fotobox mit verbauter Kamera.',
  },
];

const resourceUsageOptions = [
  { label: 'RENTAL', value: 'RENTAL' },
  { label: 'SALE', value: 'SALE' },
  { label: 'CONSUMABLE', value: 'CONSUMABLE' },
];

export type ShareGroup = {
  category: string;
  messages: string[];
};

const getSharedMessageGroupCategoryOptions = (name: string) => [
  {
    label: 'Allgemein',
    value: `Wie organisierst du eigentlich deine ganzen Kundendaten? Ich nutze dafür bookitup! Es ist eine 360° Eventverwaltungssoftware, die für dich alles im Blick behält. Mit bookitup kannst Du Angebote und Rechnungen schreiben, Notizen und Dateien hinterlegen und sogar online Verträge anbieten. Das Beste daran ist, dass bookitup auch einige Automatisierungsfunktionen bietet, die Dir viel Zeit und Mühe sparen können. \n\nIch persönlich nutze bookitup für alle meine Eventplanungen und ich muss sagen, ich bin mehr als zufrieden damit. Es ist einfach zu bedienen und spart mir jede Menge Zeit. Ich habe meine Arbeitsprozesse optimiert und kann mich auf das Wesentliche konzentrieren - nämlich ein großartiges Event zu organisieren.\n\nProbiere es doch einfach mal aus und lasse mich wissen, was Du davon hältst. Über folgenden Link kannst Du Dich registrieren: \n\n`,
  },
  {
    label: 'DJs',
    value: `Hey Leute, \n\nich möchte Euch heute eine geniale Software vorstellen, die das Planen von Events um einiges einfacher und schneller macht. Es handelt sich um bookitup, die 360° Eventverwaltungssoftware, die ich als DJ seit einiger Zeit nutze und absolut empfehlen kann. \n\nMit bookitup kann ich nicht nur Angebote und Rechnungen schreiben, sondern auch meine Playlist und meine Termine verwalten. Auch das Speichern von Notizen und Dateien sowie das Anbieten von Online-Verträgen wird von bookitup unterstützt. Und das Beste: bookitup bietet auch einige Automatisierungsfunktionen, die mir viel Zeit und Arbeit ersparen.\n\nAls DJ ist es wichtig, dass ich meine Events im Griff habe und jederzeit den Überblick behalte. Mit bookitup kann ich meine Arbeitsprozesse optimieren und mich auf das Wesentliche konzentrieren - nämlich auf die Musik und die Stimmung auf dem Event.\n\nIch kann Euch bookitup also wirklich nur empfehlen, probiert es aus und erleichtert Euch das Event-Management.\n\nKeep the party going!\n\nViele Grüße,\n${name}\n\n`,
  },
  {
    label: 'Fotografen',
    value: `Hallo liebe Kollegen,\n\nich wollte Euch heute eine Software empfehlen, die mir als Hochzeitsfotograf sehr dabei geholfen hat, meine Arbeit besser zu organisieren und zu verwalten. Es handelt sich um bookitup, die 360° Eventverwaltungssoftware, die ich seit einiger Zeit nutze und die ich absolut empfehlen kann. \n\nMit bookitup kann ich Angebote und Rechnungen schreiben, meine Termine und Aufträge verwalten und auch Notizen und Dateien speichern. Besonders praktisch finde ich auch die Möglichkeit, Online-Verträge anzubieten und zu verwalten. Und das Beste: bookitup bietet auch einige Automatisierungsfunktionen, die meine Arbeitsprozesse enorm vereinfacht haben. \n\nAls Hochzeitsfotograf ist es wichtig, dass ich stets den Überblick über meine Termine und Aufträge habe, und mit bookitup ist das einfach und unkompliziert möglich. Die Software ist sehr intuitiv und einfach zu bedienen, und hat mir schon viel Zeit und Nerven gespart. \n\nIch kann Euch bookitup also wirklich nur empfehlen, probiert es aus und erleichtert Euch die Hochzeitsfotografie.\n\nViele Grüße,\n${name}\n\n`,
  },
  {
    label: 'Sänger',
    value: `Hey Leute,\n\nich wollte Euch heute eine großartige Software empfehlen, die mir als Sänger sehr dabei geholfen hat, meine Auftritte besser zu organisieren und zu verwalten. Es handelt sich um bookitup, die 360° Eventverwaltungssoftware, die ich seit einiger Zeit nutze und absolut empfehlen kann. \n\nMit bookitup kann ich Angebote und Rechnungen schreiben, meine Termine und Aufträge verwalten und auch Notizen und Dateien speichern. Besonders praktisch finde ich auch die Möglichkeit, Online-Verträge anzubieten und zu verwalten. Und das Beste: bookitup bietet auch einige Automatisierungsfunktionen, die meine Arbeitsprozesse enorm vereinfacht haben. \n\nAls Sänger ist es wichtig, dass ich meine Auftritte und Termine gut koordiniere und den Überblick behalte. Mit bookitup kann ich meine Arbeitsprozesse optimieren und mich auf das Wesentliche konzentrieren - nämlich auf meine Musik und meine Auftritte. \n\nIch kann Euch bookitup also wirklich nur empfehlen, probiert es aus und erleichtert Euch das Event-Management.\n\nViele Grüße,\n${name}\n\n`,
  },
];

const contactTypeOptions = [
  { label: '👤 Privat', value: 'PRIVATE' },
  { label: '🏢 Firma', value: 'COMPANY' },
];

const getContactsOptions = (contacts: Contact[]) => contacts.map(getContactOption);

const getContactOption = (contact: Contact) => {
  const { id, personDescription } = contact;
  return { value: id, label: personDescription || '' };
};

const getAttributeTypesOptions = (i18n: I18n) => [
  { value: 'STRING', label: i18n._('custom-attribute.string') },
  { value: 'TEXT', label: i18n._('custom-attribute.text') },
  { value: 'SINGLE_CHOICE', label: i18n._('custom-attribute.single_choice') },
  { value: 'MULTIPLE_CHOICE', label: i18n._('custom-attribute.multiple_choice') },
  { value: 'BOOLEAN', label: i18n._('custom-attribute.boolean') },
  { value: 'DATE', label: i18n._('custom-attribute.date') },
  { value: 'TIME', label: i18n._('custom-attribute.time') },
  { value: 'NUMBER', label: i18n._('custom-attribute.number') },
  { value: 'DECIMAL', label: i18n._('custom-attribute.decimal') },
  { value: 'FILE_UPLOAD', label: i18n._('custom-attribute.file_upload') },
];

const getAttributeTypeOptions = (i18n: I18n, includeSelect?: boolean) => [
  { value: 'BOOLEAN', label: i18n._('custom-attribute.boolean') },
  { value: 'STRING', label: i18n._('custom-attribute.string') },
  { value: 'TEXT', label: i18n._('custom-attribute.text') },
  ...(includeSelect ? [{ value: 'SELECT', label: i18n._('custom-attribute.select') }] : []),
  { value: 'DATE', label: i18n._('custom-attribute.date') },
  { value: 'TIME', label: i18n._('custom-attribute.time') },
  { value: 'NUMBER', label: i18n._('custom-attribute.number') },
  { value: 'DECIMAL', label: i18n._('custom-attribute.decimal') },
  { value: 'MULTIPLE_CHOICE', label: i18n._('custom-attribute.multiple_choice') },
];

const getAttributeGroupsOptions = (groups: CustomAttributeGroupDTO[]) =>
  groups.map((group) => ({ value: group.id, label: group.name }));

const distanceRadiusOptions = [
  { label: '< 50 km', value: '50' },
  { label: '< 100 km', value: '100' },
  { label: '< 150 km', value: '150' },
  { label: '< 200 km', value: '200' },
];

const getActionTypeData = (i18n: I18n, alternativeStep: boolean) => {
  const options = [
    { value: 'SEND_MAIL', label: i18n._('actionType.SEND_MAIL') },
    { value: 'CREATE_DOCUMENT', label: i18n._('actionType.CREATE_DOCUMENT') },
    { value: 'CREATE_QUESTIONNAIRE', label: i18n._('actionType.CREATE_QUESTIONNAIRE') },
    { value: 'CREATE_CONTRACT', label: i18n._('actionType.CREATE_CONTRACT') },
    { value: 'CREATE_SHARE', label: i18n._('actionType.CREATE_SHARE') },
    { value: 'SHARE_ENTITY', label: i18n._('actionType.SHARE_ENTITY') },
    { value: 'CHANGE_EVENT_STATUS', label: i18n._('actionType.CHANGE_EVENT_STATUS') },
    { value: 'CREATE_TASK', label: i18n._('actionType.CREATE_TASK') },
    { value: 'COMPLETE_TASK', label: i18n._('actionType.COMPLETE_TASK') },
    { value: 'DELETE_TASK', label: i18n._('actionType.DELETE_TASK') },
    { value: 'DELETE_TASKS', label: i18n._('actionType.DELETE_TASKS') },
    { value: 'PUBLISH_INVOICE', label: i18n._('actionType.PUBLISH_INVOICE') },
    { value: 'WAIT_UNTIL', label: i18n._('actionType.WAIT_UNTIL') },
  ];
  if (alternativeStep) {
    options.pop();
  }
  return options;
};

const getChronoUnitsData = (i18n: I18n) => [
  { value: 'MINUTES', label: i18n._('chronoUnit.MINUTES') },
  { value: 'HOURS', label: i18n._('chronoUnit.HOURS') },
  { value: 'DAYS', label: i18n._('chronoUnit.DAYS') },
  { value: 'WEEKS', label: i18n._('chronoUnit.WEEKS') },
  { value: 'MONTHS', label: i18n._('chronoUnit.MONTHS') },
];

const getExecutionStrategyOptions = (i18n: I18n) => [
  { value: 'AFTER_PREVIOUS_STEP', label: i18n._('execution.AFTER_PREVIOUS_STEP') },
  { value: 'BEFORE_EVENT_START', label: i18n._('execution.BEFORE_EVENT_START') },
  { value: 'AFTER_EVENT_START', label: i18n._('execution.AFTER_EVENT_START') },
  { value: 'BEFORE_EVENT_END', label: i18n._('execution.BEFORE_EVENT_END') },
  { value: 'AFTER_EVENT_END', label: i18n._('execution.AFTER_EVENT_END') },
];

const getEntityReferencesOptions = (availableEntities: EntityReferenceDTO[]) =>
  availableEntities.map((entity) => ({ value: entity.id, label: entity.label }));

const mailRecipientTypeOptions = [
  { value: 'PRIMARY_CONTACT', label: 'Hauptansprechpartner' },
  // { value: 'ALL_PARTICIPANTS', label: 'Alle Teilnehmer' },
];

const getMessageTemplateOptions = (templates: MessageTemplateDto[], i18n: I18n) => {
  const mapped: LabelValuePair[] = templates.map((template) => {
    const { id, messageType, name } = template;
    return {
      value: id.toString(),
      label: messageType === 'CUSTOM' ? name : i18n._(`message.type.${messageType.toLowerCase()}`),
    };
  });
  mapped.sort(compareByLabel);
  return mapped;
};

const compareByLabel = (a: LabelValuePair, b: LabelValuePair) => {
  if (a.label > b.label) return 1;
  if (a.label < b.label) return -1;
  return 0;
};

const getObservableEventOptions = (i18n: I18n) => [
  { value: 'CONTRACT_SIGNED', label: i18n._('events.CONTRACT_SIGNED') },
  { value: 'OFFER_CONFIRMED', label: i18n._('events.OFFER_CONFIRMED') },
  { value: 'QUESTIONNAIRE_FILLED', label: i18n._('events.QUESTIONNAIRE_FILLED') },
  { value: 'INVOICE_NOT_PAID', label: i18n._('events.INVOICE_NOT_PAID') },
  { value: 'INVOICE_PAID', label: i18n._('events.INVOICE_PAID') },
  { value: 'OFFER_OVERDUE', label: i18n._('events.OFFER_OVERDUE') },
  { value: 'MAIL_RECEIVED', label: i18n._('events.MAIL_RECEIVED') },
];

const getEventKindsOptions = (kinds: EventKind[]) => kinds.map((k) => ({ value: k.id, label: k.name }));

const getCustomAttributeOptions = (customAttributes: CustomAttributeWithGroupNameDTO[]) =>
  customAttributes.map((attribute) => {
    const { name } = attribute;
    return { ...attribute, value: name, label: name };
  });

const getCustomAttributeOptionsV2 = (customAttributes: CustomAttributeWithGroupNameDTO[]) =>
  customAttributes.map((attribute) => {
    const { id, name } = attribute;
    return { ...attribute, value: id, label: name };
  });

const getResourceOptions = (resources: Resource[]) =>
  resources.map((resource) => {
    const { name, id } = resource;
    return { ...resource, value: id, label: name };
  });

const getQuestionnaireTemplatesOptions = (templates: QuestionnaireTemplateDto[]) =>
  templates.map((template) => ({ value: template.id, label: template.name }));

const getContractTemplatesOptions = (templates: any[]) =>
  templates.map((template) => ({ value: template.id.toString(), label: template.name }));

const bookitupFormStyleOptions = [
  // { label: 'Brand', value: SemanticFormStyle.bookitup },
  // { label: 'Cosmos', value: SemanticFormStyle.cosmos },
  // { label: 'Lily', value: SemanticFormStyle.lily },
  // { label: 'Minimal', value: SemanticFormStyle.minimal },
  { label: 'Dunkel', value: SemanticFormStyle.DARK },
  { label: 'Hell', value: SemanticFormStyle.LIGHT },
  { label: 'Beige/Braun', value: SemanticFormStyle.BROWN },
  { label: 'Grau/Weiß', value: SemanticFormStyle.GRAY },
];

const buttonPositionOptions = [
  { label: 'Links', value: 'left' },
  { label: 'Zentriert', value: 'center' },
  { label: 'Rechts', value: 'right' },
];

const getMailProviderOptions = (i18n: I18n) => [
  { value: 'gmail', label: i18n._('settings.mail.provider.gmail') },
  { value: 'gmx', label: i18n._('settings.mail.provider.gmx') },
  { value: 'strato', label: i18n._('settings.mail.provider.strato') },
  { value: 'web-de', label: i18n._('settings.mail.provider.web-de') },
  { value: 'office365', label: i18n._('settings.mail.provider.office365') },
  { value: 'outlook', label: i18n._('settings.mail.provider.outlook') },
  { value: 'hotmail', label: i18n._('settings.mail.provider.hotmail') },
  { value: 'djbaukasten', label: i18n._('settings.mail.provider.djbaukasten') },
  { value: 't-online', label: i18n._('settings.mail.provider.t-online') },
  { value: '1und1', label: i18n._('settings.mail.provider.1und1') },
  { value: 'jimdo', label: i18n._('settings.mail.provider.jimdo') },
  { value: 'yahoo', label: i18n._('settings.mail.provider.yahoo') },
  { value: '1blu', label: i18n._('settings.mail.provider.1blu') },
  { value: 'united-domains', label: i18n._('settings.mail.provider.united-domains') },
  { value: 'hetzner', label: i18n._('settings.mail.provider.hetzner') },
  { value: 'mail-de', label: i18n._('settings.mail.provider.mail-de') },
  { value: 'icloud', label: i18n._('settings.mail.provider.icloud') },
  { value: 'freenet', label: i18n._('settings.mail.provider.freenet') },
  { value: 'goneo', label: i18n._('settings.mail.provider.goneo') },
  { value: 'webador', label: i18n._('settings.mail.provider.webador') },
  { value: 'one-com', label: i18n._('settings.mail.provider.one-com') },
  { value: 'ovh', label: i18n._('settings.mail.provider.ovh') },
  { value: 'other', label: i18n._('settings.mail.provider.other') },
];

const getMailPortOptions = () => [
  { value: '25', label: '25' },
  { value: '465', label: '465' },
  { value: '587', label: '587' },
];

const getRecipientOptions = (contacts: Contact[], message: MessageDTO) => {
  const availableOptions: any[] = contacts
    .filter((c) => c.email)
    .map((c) => ({
      ...c,
      value: c.email,
      label: c.personDescription ?? c.email,
    }));

  const exists = (email: string) => availableOptions.find((o) => o.value === email) !== undefined;

  const { to, cc, bcc } = message;
  if (to) {
    to.forEach((id) => {
      if (!exists(id.email)) {
        availableOptions.push({
          personDescription: id.personDescription ?? id.email,
          value: id.email,
          label: id.email,
        });
      }
    });
  }
  if (cc) {
    cc.forEach((id) => {
      if (!exists(id.email)) {
        availableOptions.push({
          personDescription: id.personDescription ?? id.email,
          value: id.email,
          label: id.email,
        });
      }
    });
  }
  if (bcc) {
    bcc.forEach((id) => {
      if (!exists(id.email)) {
        availableOptions.push({
          personDescription: id.personDescription ?? id.email,
          value: id.email,
          label: id.email,
        });
      }
    });
  }
  return availableOptions;
};

const getEventStateOptions = (states: CustomEventState[], isRecommendation = false) =>
  states
    .filter((s) => (isRecommendation ? s.originState !== BookingState.UNAVAILABLE : true))
    .map((state) => {
      const { label } = state;
      return { value: label, label };
    });

const getEventStateOptionsV2 = (states: CustomEventState[]) =>
  states.map((state) => {
    const { id, label, originState } = state;
    return { value: id ?? originState, label };
  });

const getLocationOptions = (locations: LocationDto[]) =>
  locations.map((location) => {
    const { id } = location;
    const label = LocationS.getDescription(location) ?? '';
    return { value: id ? id.toString() : '', label };
  });

const getEventDatesOptions = (eventDates: EventDate[]) =>
  eventDates.map((eventDate) => {
    const { id, date, timeFrom, timeTo } = eventDate;
    const label = `${EventS.formatEventDate(date)}${timeFrom && timeTo ? `, ${timeFrom} - ${timeTo}` : ''}`;
    return { value: id.toString(), label };
  });

const eventDateTypeOpts = [
  {
    value: 'REGULAR_DATE',
    label: 'Event-Zeitraum',
    description: 'Zeitraum, indem das Event stattfindet bzw. indem du eingeplant bist.',
  },
  { value: 'APPOINTMENT', label: 'Besprechung', description: 'Termine wie Vorbesprechungen oder Vorbereitungen' },
];

const scheduleMailOptions = [
  { label: 'In 1 Stunde', value: 'HOUR' },
  { label: 'In 2 Stunden', value: 'TWO_HOUR' },
  { label: 'Morgen früh (8 Uhr)', value: 'TOMORROW_MORNING' },
  { label: 'Morgen Nachmittag (13 Uhr)', value: 'TOMORROW_AFTERNOON' },
  { label: 'Benutzerdefiniert', value: 'CUSTOM' },
];

const mailProviderHints = [
  {
    provider: 'gmail',
    appSpecificPasswordLink: 'https://myaccount.google.com/u/1/apppasswords',
    twoStepVerificationLink: 'https://myaccount.google.com/u/1/signinoptions/two-step-verification',
  },
  {
    provider: 'gmx',
    text: 'Zudem musst Du in den E-Mail-Einstellungen von GMX unter "POP3/IMAP Abruf" die Checkbox "POP3 und IMAP Zugriff erlauben" aktivieren. Um die E-Mail-Einstellungen zu öffnen, gehe im GMX Webmailer auf Deine E-Mails und klicke unten rechts auf das Zahnrad.',
    appSpecificPasswordLink: 'https://hilfe.gmx.net/sicherheit/2fa/anwendungsspezifisches-passwort.html',
    twoStepVerificationLink: null,
  },
  {
    provider: 'web.de',
    text: 'Für diesen Anbieter musst Du in den E-Mail-Einstellungen von web.de unter "POP3/IMAP Abruf" die Checkbox "POP3 und IMAP Zugriff erlauben" aktivieren. Um die E-Mail-Einstellungen zu öffnen, gehe im web.de Webmailer auf Deine E-Mails und klicke unten rechts auf das Zahnrad.',
    appSpecificPasswordLink: undefined, // necessary?
    twoStepVerificationLink: null,
  },
  {
    provider: 't-online',
    appSpecificPasswordLink: 'https://email.t-online.de/pr?a=passwords.emailpassword',
    twoStepVerificationLink: null,
  },
  {
    provider: '1blu',
    text: 'Damit Deine 1blu Mails funktionieren muss Dein Benutzername dein (vollständiger) Postfachname sein - dies ist NICHT Deine E-Mail-Adresse. Den Postfachnamen findest du z.B. im Kundencenter von 1blu in den E-Mail-Einstellungen. In unserem Test-1blu-Konto ist dieser beispielsweise a333102_0-info',
    appSpecificPasswordLink: null,
    twoStepVerificationLink: null,
  },
  {
    provider: 'yahoo',
    text: 'Yahoo unterstützt keine Antwort-an-E-Mail-Adresse. Antworten Deiner Kunden landen damit nicht automatisch in bookitup. Daher empfehlen wir hier ein anderes E-Mail-Konto zu verwenden.',
    appSpecificPasswordLink: 'https://login.yahoo.com/account/security/app-passwords/list',
    twoStepVerificationLink: null,
  },
  // {
  //   provider: 'office365',
  //   text: 'Damit Du Dein Office365 Konto verbinden kannst, ist es notwendig, dass du das in den Office365-Einstellungen erlaubst. Kontaktiere hierzu bei Problemen gerne unseren Support.',
  //   appSpecificPasswordLink: null,
  //   twoStepVerificationLink: null,
  // },
];

const mapGoogleAccounts = (accounts: GoogleAccountDTO[]) =>
  accounts.map((acc) => ({ label: acc.email, value: acc.id.toString() }));

const getContactsOptionsV2 = (contacts: Contact[]) => contacts.map(getContactOptionV2);

const getContactOptionV2 = (contact: Contact) => {
  const { id, personDescription } = contact;
  return { value: id.toString(), label: personDescription || '' };
};

const countryOptions = [
  { value: 'BE', label: 'Belgien' },
  { value: 'BG', label: 'Bulgarien' },
  { value: 'DK', label: 'Dänemark' },
  { value: 'DE', label: 'Deutschland' },
  { value: 'EE', label: 'Estland' },
  { value: 'FI', label: 'Finnland' },
  { value: 'FR', label: 'Frankreich' },
  { value: 'GR', label: 'Griechenland' },
  { value: 'IE', label: 'Irland' },
  { value: 'IS', label: 'Island' },
  { value: 'IT', label: 'Italien' },
  { value: 'HR', label: 'Kroatien' },
  { value: 'LV', label: 'Lettland' },
  { value: 'LI', label: 'Liechtenstein' },
  { value: 'LT', label: 'Litauen' },
  { value: 'LU', label: 'Luxemburg' },
  { value: 'MT', label: 'Malta' },
  { value: 'NL', label: 'Niederlande' },
  { value: 'NO', label: 'Norwegen' },
  { value: 'AT', label: 'Österreich' },
  { value: 'PL', label: 'Polen' },
  { value: 'PT', label: 'Portugal' },
  { value: 'RO', label: 'Rumänien' },
  { value: 'SE', label: 'Schweden' },
  { value: 'CH', label: 'Schweiz' },
  { value: 'SK', label: 'Slowakei' },
  { value: 'SI', label: 'Slowenien' },
  { value: 'ES', label: 'Spanien' },
  { value: 'CZ', label: 'Tschechien' },
  { value: 'HU', label: 'Ungarn' },
  { value: 'CY', label: 'Zypern' },
  { value: 'GB', label: 'Vereinigtes Königreich' },
  { value: 'US', label: 'Vereinigtes Staaten' },
];

const getEventOptions = (bookingEvents: BookingEvent[]) =>
  bookingEvents.map((e) => ({ value: e.id.toString(), label: EventS.getAsLabel(e) }));

const monochromeColors = [
  '#FFFFFF',
  '#f7f7f7',
  '#d3d3d3',
  '#c5c5c5',
  '#a8a8a8',
  '#878787',
  '#555555',
  '#404040',
  '#252525',
  '#000000',
];

const getMailConfigOptions = (includeRelay: boolean) => {
  const opts = [];
  opts.push({
    value: 'SMTP',
    label: 'SMTP',
    description:
      'Einfache Konfiguration mit Benutzername und Passwort, jedoch höhere Wahrscheinlichkeit, dass E-Mails im Spam landen.',
  });
  if (includeRelay) {
    opts.push({
      value: 'SMTP_RELAY',
      label: 'SMTP-Relay (empfohlen)',
      description:
        'Optimale Zustellung und Du kannst zukünftig einsehen, ob deine E-Mail geöffnet oder ein Link angeklickt wurde.',
    });
  }
  opts.push({
    value: 'AZURE',
    label: 'Microsoft-Konto',
    description:
      'Falls du Office365, Microsoft Exchange, Outlook oder Hotmail hast, kannst du diese Anbindung verwenden.',
  });
  return opts;
};

const getAgencyTemplateOptions = (templates: AgencyTemplateDTO[]) =>
  templates.map((t) => ({ label: t.name, value: t.id }));

const getContractHeadOptions = (i18n: I18n) => [
  { label: i18n._('contract.head.parties'), value: 'PARTIES' },
  { label: i18n._('contract.head.letterhead'), value: 'LETTERHEAD' },
  { label: i18n._('contract.head.none'), value: 'NONE' },
];

const getContractFooterOptions = (i18n: I18n) => [
  { label: i18n._('contract.footer.centered'), value: 'CENTERED' },
  { label: i18n._('contract.footer.blocks'), value: 'BLOCKS' },
  { label: i18n._('contract.footer.none'), value: 'NONE' },
];

const getContractSignOptions = (i18n: I18n) => [
  { label: i18n._('contract.sign.both'), value: 'BOTH' },
  { label: i18n._('contract.sign.none'), value: 'NONE' },
];

const getCustomerSignTypeOptions = (i18n: I18n) => [
  { label: i18n._('contract.sign.type.base64'), value: 'BASE64' },
  { label: i18n._('contract.sign.type.textField'), value: 'TEXT_FIELD' },
];

const defaultColorSwatches = [
  '#25262b',
  '#494d51',
  '#656c71',
  '#fa5252',
  '#e64980',
  '#be4bdb',
  '#7950f2',
  '#4c6ef5',
  '#228be6',
  '#15aabf',
  '#12b886',
  '#40c057',
  '#82c91e',
  '#fab005',
  '#fd7e14',
];

const amountPlaceholderOptions = [
  {
    value: '{event_hours}',
    label: 'Veranstaltungsstunden',
    description: 'Fügt automatisch die gesamte Anzahl der für die Veranstaltung geplanten Stunden ein.',
  },
  {
    value: '{location_distance}',
    label: 'Einfache Entfernung (km)',
    description: 'Trägt die einfache Entfernung von deinem Standort zur Veranstaltung in Kilometern ein.',
  },
  {
    value: '{location_double_distance}',
    label: 'Hin- und Rückweg (km)',
    description: 'Beinhaltet die gesamte Entfernung zur Veranstaltung und den Rückweg nach Hause, in Kilometern.',
  },
  {
    value: '{location_quadruple_distance}',
    label: 'Location Entfernung vierfach (km)',
    description: 'Beinhaltet die 4-fache Entfernung zur Veranstaltung, in Kilometern.',
  },
  {
    value: '{event_days}',
    label: 'Anzahl der Event-Tage',
    description: 'Die Anzahl der Event-Tage.',
  },
  {
    value: '{event_nights}',
    label: 'Anzahl der Nächte',
    description: 'Die Anzahl der Nächste eines Events.',
  },
];

const booleanOptions = [
  { label: 'Ja', value: 'true' },
  { label: 'Nein', value: 'false' },
  { label: 'Keine', value: 'null' },
];

const emailReceiverOptions = [
  {
    value: 'PRIMARY_CONTACT',
    label: 'Hauptkunde',
    description: 'Die E-Mail wird an den Hauptkunden gesendet.',
  },
  {
    value: 'CUSTOM_ADDRESS',
    label: 'Andere',
    description: 'Sende die E-Mail an eine von Dir vorgegebene E-Mail-Adresse.',
  },
];

const getMinimumLengthOptions = (i18n: I18n): any => [
  { label: i18n._('sequential-number.minimum-length.undefined'), value: '1' },
  { label: i18n._('sequential-number.minimum-length.2'), value: '2' },
  { label: i18n._('sequential-number.minimum-length.3'), value: '3' },
  { label: i18n._('sequential-number.minimum-length.4'), value: '4' },
  { label: i18n._('sequential-number.minimum-length.5'), value: '5' },
  { label: i18n._('sequential-number.minimum-length.6'), value: '6' },
];

// eslint-disable-next-line import/prefer-default-export
export const DataS = {
  fontOptions,
  documentLayoutMarginTopOptions,
  documentLayoutBottomMarginPageScopeOptions,
  getContactNameOptions,
  logoSizeOptions,
  headerFontSizeOptions,
  defaultFontSizeOptions,
  subtextFontSizeOptions,
  profileInfoFontSizeOptions,
  boxPlacementOptions,
  pageNumberPositionOptions,
  eventSortingOptions,
  recommendationOrderOptions,
  getBookingStateOptions,
  servicesOptions,
  getServiceOptions,
  howYouKnowUsOptions,
  billingPlans,
  billingAddons,
  billingIntervals,
  paymentUnitOptions,
  getDocumentTypeOptions,
  discountUnitOptions,
  getDiscountUnitOptions,
  currencyOptions,
  netGrossOptions,
  documentLanguageOptions,
  taxRateOptions,
  positionTypeOptions,
  getTaxRateOptions,
  getTaxRatesByCountry,
  shippingTypeOptions,
  getAccountingWebsiteOptions,
  documentStatusOptions,
  documentInvoicePaymentStatusOptions,
  removeEmptyValues,
  resourceTypeOptions,
  resourceUsageOptions,
  getSharedMessageGroupCategoryOptions,
  contactTypeOptions,
  getContactOption,
  getContactsOptions,
  getAttributeTypesOptions,
  getAttributeTypeOptions,
  getAttributeGroupsOptions,
  distanceRadiusOptions,
  getActionTypeData,
  getChronoUnitsData,
  getExecutionStrategyOptions,
  getEntityReferencesOptions,
  mailRecipientTypeOptions,
  getMessageTemplateOptions,
  getObservableEventOptions,
  getEventKindsOptions,
  getCustomAttributeOptions,
  getCustomAttributeOptionsV2,
  getResourceOptions,
  getQuestionnaireTemplatesOptions,
  getContractTemplatesOptions,
  bookitupFormStyleOptions,
  buttonPositionOptions,
  getRecipientOptions,
  getMailProviderOptions,
  getMailPortOptions,
  getEventStateOptions,
  getEventStateOptionsV2,
  positionUnitSuggestions,
  positionAmountSuggestions,
  getContractHeadOptions,
  getContractFooterOptions,
  getContractSignOptions,
  getCustomerSignTypeOptions,
  getLocationOptions,
  getEventDatesOptions,
  eventDateTypeOpts,
  scheduleMailOptions,
  getAgencyTemplateOptions,
  mailProviderHints,
  mapGoogleAccounts,
  getContactsOptionsV2,
  countryOptions,
  getEventOptions,
  monochromeColors,
  getMailConfigOptions,
  defaultColorSwatches,
  amountPlaceholderOptions,
  booleanOptions,
  emailReceiverOptions,
  getMinimumLengthOptions,
};
